import { useQuery } from '@tanstack/react-query';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { OrgManager } from '@/components/org-manager';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';

interface ProfileOrg {
  id: string;
  name: string;
  slug: string | null;
  created_at: string;
  role: string;
  joined_at: string;
}

export default function ProfilePage(): import('react/jsx-runtime').JSX.Element {
  const { user, isLoading: profileLoading } = useAuth();

  const { data: membershipsData } = useQuery({
    queryKey: ['org-memberships'],
    queryFn: () => api.getOrgMemberships(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: subData } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.getSubscription(),
    staleTime: 5 * 60 * 1000,
  });

  if (profileLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-foreground" />
      </div>
    );
  }

  const orgs: ProfileOrg[] = (membershipsData?.orgMemberships ?? [])
    .filter((row): boolean => !(row.org?.id == null))
    .map((row) => ({
      id: row.org!.id!,
      name: row.org!.name ?? '',
      slug: row.org!.slug ?? null,
      created_at: row.org!.createdAt ?? new Date(0).toISOString(),
      role: row.role ?? 'viewer',
      joined_at: row.joinedAt ?? new Date(0).toISOString(),
    }));

  const activeOrgId = user?.primaryOrganization?.id ?? orgs[0]?.id ?? null;
  const currentPlan = subData?.subscription?.plan ?? 'free';
  const activeOrgEntry =
    activeOrgId != null ? orgs.find((o): boolean => o.id === activeOrgId) : orgs[0];
  const activeOrg = activeOrgEntry ? { name: activeOrgEntry.name } : null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 space-y-6">
      <h1 className="text-lg font-semibold">Profile</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Email</span>
            <span className="text-sm">{user?.email}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">User ID</span>
            <code className="text-xs font-mono text-muted-foreground">{user?.id}</code>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Auth Provider</span>
            <Badge variant="secondary" className="text-xs">
              email
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Member since</span>
            <span className="text-sm text-muted-foreground">
              {orgs[0]?.joined_at != null ? new Date(orgs[0].joined_at).toLocaleDateString() : '—'}
            </span>
          </div>
        </CardContent>
      </Card>

      <OrgManager initialOrgs={orgs} activeOrgId={activeOrgId} />

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Badge className="bg-[var(--flux-accent)]/15 text-[var(--flux-accent)] border-[var(--flux-accent)]/30 capitalize">
              {currentPlan}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {currentPlan === 'free'
                ? 'Unlimited projects · 7 day retention · Community support'
                : currentPlan === 'pro'
                  ? 'Unlimited projects · 30 day retention · Priority support'
                  : 'Unlimited projects · 90 day retention · 24/7 dedicated support'}
            </span>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {currentPlan === 'free' ? (
              <>
                Upgrade to Pro for longer retention, priority support, and more.{' '}
                <a
                  href="/subscription"
                  className="underline underline-offset-2 hover:text-foreground transition-colors"
                >
                  View plans
                </a>
              </>
            ) : (
              <a
                href="/subscription"
                className="underline underline-offset-2 hover:text-foreground transition-colors"
              >
                Manage your subscription
              </a>
            )}
          </p>
          {activeOrg ? (
            <p className="mt-4 text-xs text-muted-foreground">
              Active organization:{' '}
              <span className="font-mono text-foreground/80">{activeOrg.name}</span>
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
